import { Suspense, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PageLoader } from '@customerio/pluma-components/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ThemeContextProvider } from '@/context/theme';
import Authenticated from '@/layouts/authenticated';
import DesignStudio, { DesignStudioNonBackgroundApp } from '@/pages/design-studio';
import NotFoundPage from '@/pages/not-found.page';
import { handleWorkspaceTransition } from '@/services/db/shared/workspace-cache-sync';
import { useLocation } from '@/services/router/compat';
import { getWorkspaceIdParam } from '@/services/router/workspace-id-param';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { AppBundle, AppBundleRoutes } from '@/utils/app-bundles';
import { Navigate } from '@/utils/navigate';
import { AssetLibrary } from './-route-pages';

type LazyPageProps = {
  children: ReactNode;
};

export function LazyPage({ children }: LazyPageProps) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export function AuthenticatedLayout() {
  return <Authenticated />;
}

export function WorkspaceRoot() {
  const location = useLocation();
  // Derive the workspace id from the pathname, not route params: switching
  // workspaces within the same app stays in the same bundle, which the
  // embedded-app history bridge syncs without a TanStack Router load. Route
  // matches/params go stale, so an effect keyed on params would never re-run
  // and the workspace-transition cache clear would silently never fire on the
  // most common switch path (CON-7180).
  const wsId = parseWorkspaceIdParam(getWorkspaceIdParam(location.pathname));
  const qc = useQueryClient();
  useEffect(() => {
    // A null id means the workspace segment is unresolved — the `/last`
    // sentinel before LastWorkspaceResolver redirects, or a non-numeric id.
    // Don't treat that as leaving the workspace: it would clear the current
    // workspace's cache only to immediately re-enter the same workspace once
    // `last` resolves. Wait for a real id so `previousWorkspaceId` always
    // tracks the last real workspace.
    if (wsId == null) return;
    handleWorkspaceTransition(qc, wsId);
  }, [qc, wsId]);
  return (
    <>
      <Outlet />
      <Suspense>
        <AssetLibrary />
      </Suspense>
    </>
  );
}

function parseWorkspaceIdParam(workspaceId: string | null | undefined): number | null {
  if (workspaceId == null) return null;
  const n = Number(workspaceId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

type LastWorkspaceResolverProps = {
  appBundle?: AppBundle;
  children: ReactNode;
};

function getLocationSuffix(location: { hash?: string; searchStr: string }) {
  const hash =
    location.hash == null || location.hash === '' || location.hash.startsWith('#')
      ? location.hash
      : `#${location.hash}`;
  return `${location.searchStr}${hash ?? ''}`;
}

export function LastWorkspaceResolver({ appBundle, children }: LastWorkspaceResolverProps) {
  const location = useLocation();
  const router = useRouter();
  const currentWorkspace = useCurrentWorkspace();

  // Derive the workspace id from the location pathname rather than route params.
  // Embedded apps (e.g. Design Studio) navigate within their own bundle without
  // triggering a TanStack Router load (see services/router/embedded-app-router-bridge),
  // so route matches/params go stale on a same-bundle navigation while the
  // location store stays in sync. Reading the pathname keeps the `last` sentinel
  // detectable so the redirect still fires (CON-7180).
  const workspaceId = getWorkspaceIdParam(location.pathname);

  const redirectPath = useMemo(() => {
    if (workspaceId !== 'last') {
      return null;
    }

    if (currentWorkspace == null) {
      return null;
    }

    if (appBundle != null) {
      const appPath = AppBundleRoutes[appBundle];
      const appPathWithSlash = `${appPath}/`;
      const appPathIndex = location.pathname.indexOf(appPathWithSlash);
      const subPath = appPathIndex !== -1 ? location.pathname.slice(appPathIndex + appPathWithSlash.length) : '';
      const workspacePath = router.buildLocation({
        to: '/workspaces/$workspaceId',
        params: { workspaceId: currentWorkspace.id },
      }).href;
      const redirectPath = [workspacePath, appPath.replace(/^\//, ''), subPath].filter(Boolean).join('/');
      return redirectPath + getLocationSuffix(location);
    }

    const workspacePath = router.buildLocation({
      to: '/workspaces/$workspaceId',
      params: { workspaceId: currentWorkspace.id },
    }).href;
    return location.pathname.replace('/workspaces/last', workspacePath) + getLocationSuffix(location);
  }, [workspaceId, currentWorkspace, location.hash, location.pathname, location.searchStr, appBundle, router]);

  if (redirectPath != null) {
    // Resolving the `last` sentinel to a real workspace id is a shell-level URL
    // normalization that must complete before the embedded app mounts. SafeNavigate
    // routes embedded-bundle transitions through the child-app load/confirm handshake,
    // but during this redirect the embedded app is not mounted yet (the resolver
    // renders this redirect instead of the app), so `childAppLoaded` stays false and
    // the transition parks forever. For embedded bundles, navigate directly and let
    // the embedded-app history bridge resolve it; hydra routes keep the SafeNavigate
    // transition machinery (their active bundle is null, so they never park).
    return appBundle != null ? (
      <Navigate to={redirectPath} replace={true} />
    ) : (
      <SafeNavigate to={redirectPath} options={{ shouldReplaceHistoryEntry: true }} />
    );
  }

  return children;
}

export function DesignStudioRoute() {
  return (
    <LastWorkspaceResolver appBundle={AppBundle.DesignStudio}>
      <DesignStudio />
    </LastWorkspaceResolver>
  );
}

export function EmbedDesignStudioRoute() {
  return <DesignStudioNonBackgroundApp />;
}

export function ThemedNotFoundRoute() {
  return (
    <ThemeContextProvider>
      <NotFoundPage />
    </ThemeContextProvider>
  );
}
